import { z } from 'zod';

import { calendarDateSchema } from './employee';
import { RECORD_SORTS, listQuerySchema, sortParam } from './pagination';

/*
 * Invoicing — ROADMAP.md §12, business-rules.md §7 (DECIDED), data-model.md §11.
 *
 * **Every amount crossing this boundary is an integer number of cents**, and
 * every total in a request body is decoration: the server recomputes subtotal,
 * tax and total from the line items on every write and ignores anything the
 * browser claims (business-rules.md §7). The one non-integer is `quantity`,
 * which is hours, not money.
 *
 * **No raw enum reaches a screen** (P5): `status` and `method` both carry label
 * maps and every view sends the label beside the value.
 *
 * **There is no online payment field anywhere in this file, deliberately** —
 * no provider, no token, no redirect URL. Manual entry is the payment model
 * (owner, 2026-08-03).
 */

// ---------------------------------------------------------------------------
// The vocabularies
// ---------------------------------------------------------------------------

export const INVOICE_STATUS_VALUES = ['draft', 'sent', 'paid', 'void'] as const;
export type InvoiceStatusValue = (typeof INVOICE_STATUS_VALUES)[number];

export const INVOICE_STATUS_LABELS: Record<InvoiceStatusValue, string> = {
  draft: 'Draft',
  sent: 'Sent',
  paid: 'Paid',
  void: 'Void',
};

export const PAYMENT_METHOD_VALUES = [
  'cash',
  'check',
  'bank_transfer',
  'card_in_person',
  'other',
] as const;
export type PaymentMethodValue = (typeof PAYMENT_METHOD_VALUES)[number];

export const PAYMENT_METHOD_LABELS: Record<PaymentMethodValue, string> = {
  cash: 'Cash',
  check: 'Check',
  bank_transfer: 'Bank Transfer',
  card_in_person: 'Card (in person)',
  other: 'Other',
};

// ---------------------------------------------------------------------------
// Input
// ---------------------------------------------------------------------------

/**
 * Cents, as a whole number. The cap is $100,000 — far below the `int` column's
 * ceiling, so an absurd amount fails in words rather than in arithmetic (the
 * same reasoning as the expense cap).
 */
const unitPriceCentsSchema = z
  .number()
  .int('Amounts are whole cents')
  .min(0, 'A price cannot be negative')
  .max(10_000_000, 'That is over $100,000 — check the amount');

/**
 * Hours (or 1 for flat). Two decimal places is what the column holds; more is
 * a typo, rejected rather than silently rounded — the same rule as
 * `parseMoneyInput`. The 999.99 ceiling keeps quantity × price safely inside
 * the `total_cents` int even at the price cap.
 */
const quantitySchema = z
  .number()
  .gt(0, 'Quantity must be more than zero')
  .max(999.99, 'That is over 999 hours — check the quantity')
  .refine((value) => Math.round(value * 100) === value * 100, {
    message: 'Quantities go to two decimal places — 2.5, not 2.505',
  });

const lineDescriptionSchema = z
  .string()
  .trim()
  .min(1, 'Every line needs a description')
  .max(512, 'Must be 512 characters or fewer');

const notesSchema = z
  .string()
  .trim()
  .max(2000, 'Must be 2000 characters or fewer')
  .transform((value) => (value.length === 0 ? null : value))
  .nullable()
  .optional();

/**
 * Creating an invoice: a client and the completed jobs it bills.
 *
 * §7: "an invoice belongs to a client and covers one or more completed jobs",
 * so at least one job is required *here*. Lines are freely editable afterwards
 * — the sentence describes how an invoice is born, not a constraint re-checked
 * on every edit.
 */
export const createInvoiceSchema = z.object({
  clientId: z.uuid(),
  jobIds: z.array(z.uuid()).min(1, 'Pick at least one completed job').max(100),
});
export type CreateInvoiceInput = z.infer<typeof createInvoiceSchema>;

/** Draft-only edits. Totals are not here because the server computes them. */
export const updateInvoiceSchema = z.object({
  taxEnabled: z.boolean().optional(),
  notes: notesSchema,
  internalNotes: notesSchema,
});
export type UpdateInvoiceInput = z.infer<typeof updateInvoiceSchema>;

/**
 * Adding a line: a catalog pick or a typed one-off, and the shape says which.
 *
 * A catalog pick sends the service id and lets the server snapshot the
 * description, unit and price from the rate card — the browser could not be
 * trusted to copy them even if it wanted to. A custom line sends everything,
 * because there is nothing to copy from (business-rules.md §7: free-text lines
 * must stay possible).
 */
export const addLineItemSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('catalog'),
    serviceTypeId: z.uuid(),
    quantity: quantitySchema.optional(),
  }),
  z.object({
    kind: z.literal('custom'),
    description: lineDescriptionSchema,
    pricingUnit: z.enum(['flat', 'hourly']),
    quantity: quantitySchema,
    unitPriceCents: unitPriceCentsSchema,
  }),
]);
export type AddLineItemInput = z.infer<typeof addLineItemSchema>;

/** Editing a line keeps its provenance; only the visible three change. */
export const updateLineItemSchema = z.object({
  description: lineDescriptionSchema.optional(),
  quantity: quantitySchema.optional(),
  unitPriceCents: unitPriceCentsSchema.optional(),
});
export type UpdateLineItemInput = z.infer<typeof updateLineItemSchema>;

/** Appending more completed jobs to a draft. Same checks as creation. */
export const addJobsSchema = z.object({
  jobIds: z.array(z.uuid()).min(1, 'Pick at least one completed job').max(100),
});
export type AddJobsInput = z.infer<typeof addJobsSchema>;

export const voidInvoiceSchema = z.object({
  reason: z
    .string()
    .trim()
    .min(1, 'Say why — a void with no reason is not a financial record')
    .max(512, 'Must be 512 characters or fewer'),
});
export type VoidInvoiceInput = z.infer<typeof voidInvoiceSchema>;

/**
 * Recording money that arrived. `paidOn` is the day it arrived — a cheque from
 * Tuesday's mail entered on Thursday is Tuesday's payment (ROADMAP.md §12.4).
 */
export const recordPaymentSchema = z.object({
  amountCents: z
    .number()
    .int('Amounts are whole cents')
    .min(1, 'A payment must be more than zero')
    .max(10_000_000, 'That is over $100,000 — check the amount'),
  method: z.enum(PAYMENT_METHOD_VALUES),
  paidOn: calendarDateSchema,
  reference: z
    .string()
    .trim()
    .max(128, 'Must be 128 characters or fewer')
    .transform((value) => (value.length === 0 ? null : value))
    .nullable()
    .optional(),
  notes: z
    .string()
    .trim()
    .max(512, 'Must be 512 characters or fewer')
    .transform((value) => (value.length === 0 ? null : value))
    .nullable()
    .optional(),
});
export type RecordPaymentInput = z.infer<typeof recordPaymentSchema>;

// ---------------------------------------------------------------------------
// Listing
// ---------------------------------------------------------------------------

/**
 * `overdue` is a filter value but not a status value: it selects sent invoices
 * past their due date, and the rows still say `sent` — derived, never stored
 * (business-rules.md §7).
 */
export const invoiceListQuerySchema = listQuerySchema.extend({
  sort: sortParam(RECORD_SORTS, 'newest'),

  status: z.enum([...INVOICE_STATUS_VALUES, 'overdue', 'all']).default('all'),
  clientId: z.uuid().optional(),
});
export type InvoiceListQuery = z.infer<typeof invoiceListQuerySchema>;

export const clientInvoiceListQuerySchema = listQuerySchema;
export type ClientInvoiceListQuery = z.infer<typeof clientInvoiceListQuerySchema>;

// ---------------------------------------------------------------------------
// Views
// ---------------------------------------------------------------------------

export interface InvoiceLineItemView {
  id: string;
  jobId: string | null;
  /** For the admin's benefit; the description already reads for the customer. */
  jobNumber: string | null;
  serviceTypeId: string | null;
  description: string;
  pricingUnit: 'flat' | 'hourly';
  /** Hours as a number — `2.5`, parsed once at the service edge. */
  quantity: number;
  unitPriceCents: number;
  totalCents: number;
  sortOrder: number;
}

/** What the customer may see of a payment. The recorder's name is not theirs. */
export interface PaymentView {
  id: string;
  amountCents: number;
  method: PaymentMethodValue;
  methodLabel: string;
  /** True when the money went back. The row stays — void, never delete. */
  isRefunded: boolean;
  reference: string | null;
  paidOn: string;
}

/** The office's view of the same payment. */
export interface AdminPaymentView extends PaymentView {
  notes: string | null;
  recordedByName: string;
  refundedAt: string | null;
}

/**
 * One row of the admin list.
 *
 * `isOverdue` is computed at read time from due date and payment state; it is
 * never a status. `balanceCents` is total minus paid, floored at zero — an
 * overpayment reads as paid, and the office's remedy is a refund.
 */
export interface AdminInvoiceListItem {
  id: string;
  invoiceNumber: string;
  clientId: string;
  clientName: string;
  status: InvoiceStatusValue;
  statusLabel: string;
  isOverdue: boolean;
  issueDate: string | null;
  dueDate: string | null;
  totalCents: number;
  amountPaidCents: number;
  balanceCents: number;
  createdAt: string;
}

export interface AdminInvoiceDetail extends AdminInvoiceListItem {
  subtotalCents: number;
  taxEnabled: boolean;
  taxRateBps: number;
  taxCents: number;
  notes: string | null;
  internalNotes: string | null;
  voidReason: string | null;
  voidedAt: string | null;
  sentAt: string | null;
  paidAt: string | null;
  /** The void this one replaces, and what replaced this void — both ways. */
  replacesInvoiceId: string | null;
  replacesInvoiceNumber: string | null;
  replacedByInvoiceId: string | null;
  replacedByInvoiceNumber: string | null;
  /** The thread that carried it, for the "View conversation" link. */
  conversationId: string | null;
  /** True once the issued PDF is stored. The key itself never leaves the server. */
  hasPdf: boolean;
  clientEmail: string | null;
  createdByName: string;
  lineItems: InvoiceLineItemView[];
  payments: AdminPaymentView[];
}

/**
 * The customer's own row. **Deliberately missing**: internal notes, the
 * office's names, the conversation id, and anything about other clients —
 * `payload-leak-sweep` plants a sentinel in the internal notes and searches
 * every response a client can obtain.
 */
export interface ClientInvoiceListItem {
  id: string;
  invoiceNumber: string;
  status: InvoiceStatusValue;
  statusLabel: string;
  isOverdue: boolean;
  issueDate: string | null;
  dueDate: string | null;
  totalCents: number;
  amountPaidCents: number;
  balanceCents: number;
}

export interface ClientInvoiceDetail extends ClientInvoiceListItem {
  subtotalCents: number;
  taxEnabled: boolean;
  taxRateBps: number;
  taxCents: number;
  notes: string | null;
  /** When this invoice was reissued, the number the customer should pay instead. */
  replacedByInvoiceNumber: string | null;
  lineItems: InvoiceLineItemView[];
  payments: PaymentView[];
  /** §1's verified facts, for the "how to pay" block. Assembled server-side. */
  remitTo: RemitTo;
}

/**
 * How to actually pay — manual methods only, from business-rules.md §1's
 * verified facts. **There is no pay button and no payment link, deliberately.**
 */
export interface RemitTo {
  businessName: string;
  phone: string;
  email: string;
  instructions: string;
}

/** The three numbers above the admin list. Always numbers — zero is `0` (P1). */
export interface InvoiceTotals {
  /** Balance still owed across every sent invoice. */
  outstandingCents: number;
  /** The slice of that balance past its due date. */
  overdueCents: number;
  draftCount: number;
}

/** A completed job the builder can still bill — not on any live invoice. */
export interface BillableJob {
  id: string;
  jobNumber: string;
  scheduledDate: string;
  serviceTypeId: string;
  serviceName: string;
  /** What the line would say: flat price, or rate × estimated hours. */
  pricingUnit: 'flat' | 'hourly';
  defaultPriceCents: number | null;
  estimatedDurationMinutes: number;
}
